// BUILD-046 increment 1b-i: the PINNED POPOVER of an initials circle - the touch (and click, and Enter / Space) way to read a full name - as a small state machine, plus where the popover goes.
// PersonAvatar.tsx draws it; the decisions are made here so they can be tested without a browser.
//
// Three ways to the full name:
//   HOVER and KEYBOARD FOCUS  open the existing name tooltip (PersonName.useNameTip). That hook keeps its own hover / focus state and its own Escape; it is NOT repeated here.
//   TAP / CLICK / Enter / Space  TOGGLE a pinned popover: it stays open until a second activation, a press outside it, Escape or the circle losing focus.
//
// Why a state machine for the pin. A touch screen does not only tap: the browser then replays a mouse (it fires mouseenter and leaves the pointer "hovering" until the next tap elsewhere)
// and, on most phones, focuses the button. If those replayed events opened the hover tooltip, the second tap would toggle the pin off while the tooltip, held open by the stuck hover and
// focus, stayed on screen, and the circle could not be closed by tapping it. So:
//   - a pointer press records its pointerType: after a TOUCH press the replayed hover is ignored (`acceptsHover`), and only the pin decides what is open;
//   - the focus tooltip opens for KEYBOARD focus only (`:focus-visible`; `acceptsFocus`): the focus a tap or a mouse click gives a button is no reason for a tooltip that would stay after the
//     pointer has gone. Keyboard focus also ends touch mode, so Tab onto a circle after touching the screen shows the tooltip and a mouse hovers again; any key press ends touch mode too;
//   - a mouse or pen press (hover capable) ends touch mode.
//
// States: { pinned, touch }. Events: pointer-down(pointerType), focus(keyboard), key-down, activate, escape, outside-press, blur. `overlayOf` says which overlay is drawn: the pinned popover wins over
// the hover / focus tooltip (never both), else the tooltip when the hook says it is open, else none.
//
// Pure module: no React, no DOM, no I/O. Erasable TypeScript only.

export interface PinState {
  /** The popover is pinned open. */
  readonly pinned: boolean;
  /** The last pointer press was a touch: replayed hover and pointer-made focus are ignored. */
  readonly touch: boolean;
}

export const INITIAL_PIN_STATE: PinState = Object.freeze({ pinned: false, touch: false });

export type PinEvent =
  | { type: 'pointer-down'; pointerType: string }
  | { type: 'focus'; keyboard: boolean }
  | { type: 'key-down' }
  /** A tap, a click, or Enter / Space on the focused circle (the button's click event). */
  | { type: 'activate' }
  | { type: 'escape' }
  /** A press on anything that is neither the circle nor its popover. */
  | { type: 'outside-press' }
  /** The circle lost focus. */
  | { type: 'blur' };

function next(state: PinState, change: Partial<{ pinned: boolean; touch: boolean }>): PinState {
  const pinned = change.pinned ?? state.pinned;
  const touch = change.touch ?? state.touch;
  return pinned === state.pinned && touch === state.touch ? state : Object.freeze({ pinned, touch });
}

/** The next state. The same object is returned when nothing changes; the input is never changed. */
export function reducePin(state: PinState, event: PinEvent): PinState {
  switch (event.type) {
    case 'pointer-down':
      return next(state, { touch: event.pointerType === 'touch' });
    case 'focus':
      return event.keyboard ? next(state, { touch: false }) : state;
    case 'key-down':
      return next(state, { touch: false });
    case 'activate':
      return next(state, { pinned: !state.pinned });
    case 'escape':
    case 'outside-press':
    case 'blur':
      return next(state, { pinned: false });
    default:
      return state;
  }
}

/** True when a (possibly replayed) mouse-enter should reach the hover tooltip. After a touch it should not. */
export function acceptsHover(state: PinState): boolean {
  return !state.touch;
}

/** True when a focus event should reach the focus tooltip: KEYBOARD focus only. The focus a tap or a click gives the button (not `:focus-visible`) does not open a tooltip. */
export function acceptsFocus(keyboard: boolean): boolean {
  return keyboard;
}

export type AvatarOverlay = 'pinned' | 'tooltip' | 'none';

/** Which overlay is drawn. `tooltipOpen` is the hover / focus tooltip's own answer (useNameTip.open). The pinned popover wins; the two are never drawn together. */
export function overlayOf(state: PinState, tooltipOpen: boolean): AvatarOverlay {
  if (state.pinned) return 'pinned';
  return tooltipOpen ? 'tooltip' : 'none';
}

// ───────────────────────── where the popover goes ─────────────────────────

export interface AnchorBox {
  top: number;
  bottom: number;
  left: number;
}

export interface BubbleSize {
  width: number;
  height: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface BubblePlace {
  top: number;
  left: number;
  /** True when the bubble is above the circle (there was no room below). */
  above: boolean;
}

export const VIEWPORT_MARGIN = 8;

/**
 * Where a fixed-position bubble goes: under the circle, level with its left edge, kept inside the viewport (8 px margin); above the circle when it would not fit below and does fit
 * above. The same rule as the name tooltip (PersonName.useNameTip), so a tooltip and a pinned popover never jump between places.
 */
export function placeBubble(anchor: AnchorBox, size: BubbleSize, viewport: ViewportSize, margin: number = VIEWPORT_MARGIN): BubblePlace {
  const left = Math.max(margin, Math.min(anchor.left, viewport.width - size.width - margin));
  const above = anchor.bottom + size.height > viewport.height - margin && anchor.top - size.height > margin;
  return { top: above ? anchor.top - size.height : anchor.bottom, left, above };
}
